import { useEffect, useRef, useState } from 'react';

const MonitorVideoSection = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
        if (entry.isIntersecting && videoRef.current) {
          videoRef.current.play();
        }
      },
      { threshold: 0.3 }
    );

    const section = document.getElementById('monitor-section');
    if (section) observer.observe(section);

    return () => observer.disconnect();
  }, []);

  return (
    <section 
      id="monitor-section" 
      className="relative py-24 md:py-32 bg-black overflow-hidden"
    >
      {/* Background noise */}
      <div className="absolute inset-0 bg-noise opacity-10"></div>
      
      {/* Section header */}
      <div className="container mx-auto px-4 mb-12 text-center">
        <p className="text-green-500 font-mono text-sm mb-2 analog-text">
          ▶ TRANSMISSION PLAYBACK
        </p>
        <h2 className="text-3xl md:text-4xl font-bold text-white glitch-text">
          Intercepted Signal Feed
        </h2>
        <p className="text-gray-400 mt-4 max-w-xl mx-auto">
          The following transmission was recovered from Station 7. Viewer discretion is advised.
        </p>
      </div>

      {/* CRT Monitor Frame */}
      <div className="container mx-auto px-4 flex justify-center">
        <div className="relative max-w-4xl w-full">
          {/* Monitor outer shell */}
          <div className="relative bg-gradient-to-b from-zinc-700 via-zinc-800 to-zinc-900 rounded-xl p-4 md:p-8 shadow-2xl">
            {/* Monitor bezel */}
            <div className="relative bg-zinc-900 rounded-lg p-2 md:p-4 border-4 border-zinc-700">
              {/* Screen frame */}
              <div className="relative bg-black rounded overflow-hidden aspect-video">
                {/* Scanline overlay */}
                <div className="absolute inset-0 pointer-events-none z-20">
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-green-500/5 to-transparent animate-pulse"></div>
                  <div 
                    className="absolute inset-0 opacity-30"
                    style={{
                      backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.3) 2px, rgba(0,0,0,0.3) 4px)',
                    }}
                  ></div>
                </div>
                
                {/* CRT curvature effect */}
                <div 
                  className="absolute inset-0 pointer-events-none z-30"
                  style={{
                    boxShadow: 'inset 0 0 100px rgba(0,0,0,0.5), inset 0 0 30px rgba(0,0,0,0.3)',
                    borderRadius: '4px',
                  }}
                ></div>

                {/* Video */}
                <video
                  ref={videoRef}
                  loop
                  muted
                  playsInline
                  className={`w-full h-full object-cover transition-opacity duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
                >
                  <source src="/videos/monitor-video.mp4" type="video/mp4" />
                </video>

                {/* Static noise when not playing */}
                {!isVisible && (
                  <div className="absolute inset-0 bg-noise animate-pulse opacity-50"></div>
                )}

                {/* Screen reflection */}
                <div 
                  className="absolute inset-0 pointer-events-none z-40"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%, transparent 100%)',
                  }}
                ></div>
              </div>

              {/* Monitor controls */}
              <div className="flex items-center justify-between mt-4 px-2">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${isVisible ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
                  <span className="text-xs font-mono text-gray-500">
                    {isVisible ? 'RECEIVING' : 'STANDBY'}
                  </span>
                </div>
                <div className="flex gap-3">
                  <div className="w-4 h-4 rounded-full bg-zinc-700 border border-zinc-600"></div>
                  <div className="w-4 h-4 rounded-full bg-zinc-700 border border-zinc-600"></div>
                  <div className="w-8 h-4 rounded bg-zinc-700 border border-zinc-600"></div>
                </div>
              </div>
            </div>

            {/* Monitor brand label */}
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2">
              <span className="text-xs font-mono text-zinc-500 tracking-widest">WEIRDOBEACH™</span>
            </div>
          </div>

          {/* Monitor stand */}
          <div className="flex justify-center">
            <div className="w-24 h-8 bg-gradient-to-b from-zinc-700 to-zinc-800 rounded-b-lg"></div>
          </div>
          <div className="flex justify-center">
            <div className="w-40 h-3 bg-gradient-to-b from-zinc-800 to-zinc-900 rounded-b-xl"></div>
          </div>
        </div>
      </div>

      {/* Decorative text */}
      <div className="container mx-auto px-4 mt-12 text-center">
        <p className="text-xs font-mono text-gray-600 analog-text">
          SIGNAL SOURCE: UNKNOWN | FREQUENCY: ████ MHz | CLEARANCE: LEVEL 3 REQUIRED
        </p>
      </div>
    </section>
  );
};

export default MonitorVideoSection;
